import { Message, messageCallback } from 'ably';
import moment from 'moment';

import { Eventbus } from 'app/src/data/Eventbus';
import { sleep } from 'label/utils/async';
import store from 'pages/store';
import { updateDevices } from 'pages/store/reducers/hub';
import { getBrandingConfig } from 'pages/views/devices/utils';
import Logger from 'pages/views/devices/utils/logger';
import { getOptions, OPTIONS } from 'pages/views/devices/utils/options';
import {
  getBrandingConfigChanged,
  getHubSmartState,
  removeBrandingConfigChanged,
  removeHubEnabled,
  removeHubSmartConnection,
  safeGetItem,
  safeSetItem,
  setHubSmartState,
} from 'pages/views/devices/utils/storage';
import { setHubPrintLoadingCompleted, setHubPrintLoadingError } from 'root/apps/Hub/Receipt';
import { cancelRegisterCustomer, setRegisterCustomer } from 'root/apps/RegisterCFDCustomer/utils';

import {
  AblyStatus,
  AbstractSocket,
  CFD_MESSAGES,
  ConnectionStatus,
  Devices,
  HUB_MESSAGES,
  isCfdMessage,
  isHubMessage,
  isPrinterMessage,
  isStatusConnected,
  MEMBER_TYPE,
  PRINTER_KINDS,
  PRINTER_MESSAGES,
  Transport,
} from './types';

type Event<Type> = { event: Type; data: any };
export abstract class SocketBase {
  public status: ConnectionStatus | AblyStatus = 'Disconnected';

  protected isUnLoading = false;

  protected isSaleHidden = true;

  protected transport: Transport = null;

  static messageQueue: {
    hub: Event<HUB_MESSAGES>[];
    cfd: Event<CFD_MESSAGES>[];
    printer: Event<PRINTER_MESSAGES>[];
  } = { hub: [], cfd: [], printer: [] };

  public abstract send: AbstractSocket['send'];

  public abstract disconnect: AbstractSocket['disconnect'];

  protected getLogLevel() {
    return getOptions()[OPTIONS.IS_DEBUG_MODE] ? 2 : 0;
  }

  protected queueMessage = (event: CFD_MESSAGES | HUB_MESSAGES | PRINTER_MESSAGES, data?: any) => {
    if (event !== CFD_MESSAGES.HIDE_SALE) {
      if (isHubMessage(event)) {
        SocketBase.messageQueue.hub.push({ event, data });
      } else if (isPrinterMessage(event)) {
        SocketBase.messageQueue.printer.push({ event, data });
      } else if (isCfdMessage(event)) {
        SocketBase.messageQueue.cfd.push({ event, data });
      }

      Logger.debug(
        `${this.transport}:Hub: Connection not yet established. Enqueuing ${event} in ${this.constructor.name}`,
        data
      );
    }
  };

  protected sendMessages = async (type: 'hub' | 'cfd' | 'printer') => {
    let message = SocketBase.messageQueue[type].pop();

    if (isStatusConnected(this.status)) {
      Logger.debug(`${this.transport}:Hub: Connection established. sending events for ${type}`);
      while (message) {
        // eslint-disable-next-line no-await-in-loop
        await this.send(message.event as any, message.data);
        message = SocketBase.messageQueue[type].pop();
      }

      if (getBrandingConfigChanged()) {
        removeBrandingConfigChanged();

        await this.send(HUB_MESSAGES.UPDATE_CONFIG, {
          config: {
            ...(await getBrandingConfig()),
          },
        });
      }
    }
  };

  // Overridden in the ablyClient class
  protected isBrowserCFDConnected = async (): Promise<boolean> => false;

  protected onDevices: messageCallback<Message> = async (message) => {
    const { defaultPrinters, selectedDisplay, version } = message.data as Devices;

    if (defaultPrinters?.LABEL || defaultPrinters?.RECEIPT) {
      this.sendMessages('printer');
    }

    Logger.debug(`${this.transport}:Hub: ${HUB_MESSAGES.DEVICES}`, message.data);
    const isBrowserCFDConnected = await this.isBrowserCFDConnected();

    // version is sent on initial connect only
    const payload = version ? { version } : {};

    // Apply both printers and CFD coming from hub if BrowserCFD isn't connected
    if (!isBrowserCFDConnected) {
      Object.assign(payload, { defaultPrinters, selectedDisplay });
    }

    // Only apply printers if BrowserCFD is connected
    if (message.clientId === MEMBER_TYPE.HUB && isBrowserCFDConnected) {
      Object.assign(payload, { defaultPrinters });
    }

    // Only use display if Browser CFD is connected. Skip printers
    if (message.clientId === MEMBER_TYPE.CFD) {
      Object.assign(payload, { selectedDisplay });
    }

    store.dispatch(updateDevices(payload));
  };

  protected onGetConfig = async () => {
    await this.send(HUB_MESSAGES.UPDATE_CONFIG, {
      config: {
        ...getOptions(),
        ...(await getBrandingConfig()),
      },
    });
  };

  protected onPrintCompleted = (message: TSFixMe) => {
    setHubPrintLoadingCompleted();
    Logger.debug(`${this.transport}:Printers: onPrintCompleted`, message);
  };

  protected onPrintError = (message: TSFixMe) => {
    const kind = message.data.includes('label') ? PRINTER_KINDS.LABEL : PRINTER_KINDS.RECEIPT;

    setHubPrintLoadingError({ error: message.data, kind });
    Logger.debug(`${this.transport}:Printers: onPrintError`, message);
  };

  protected setUnloading = () => {
    this.isUnLoading = true;
    this.registerHubSmartEvents();
  };

  protected onTipSelected = (message: Message) => {
    const { data } = message;
    Logger.debug(`${this.transport}:CFD: onTipSelected`, message);
    Eventbus.emit(CFD_MESSAGES.TIP_CONFIRMED, { data });
  };

  protected onCustomerSave = (message: Message) => {
    const { data } = message;
    Logger.debug(`${this.transport}:CFD: onCustomerSave`, message);

    setRegisterCustomer(data);
  };

  protected onCustomerCancel = () => {
    cancelRegisterCustomer();
  };

  protected registerHubSmartEvents = () => {
    const state = getHubSmartState() || store.getState().hub;

    if (isStatusConnected(state.connection.hub) || isStatusConnected(state.connection.browserCFD)) {
      // Only save the important keys
      const { connection, defaultPrinters, version, selectedDisplay } = state;
      setHubSmartState({ connection, defaultPrinters, version, selectedDisplay });
    }
  };

  public getCFDEnabled = () => {
    const { selectedDisplay, connection } = store.getState().hub;
    return !!Object.keys(selectedDisplay).length || isStatusConnected(connection.browserCFD);
  };

  protected checkOnLastConnection = async () => {
    // Check if there was a successful connection AFTER connecting. Maybe the merchant was on holiday for a week.
    await sleep(5000);
    if (moment().diff(moment(this.getPreviousConnection()), 'days') > 7) {
      this.disconnect();
      removeHubEnabled();
      removeHubSmartConnection();
    }
  };

  protected getPreviousConnection = () =>
    safeGetItem(
      'HUB_PREVIOUS_SUCCESSFUL_CONNECTION_TO_CLIENT',
      moment().subtract(6, 'days').format()
    )!; // Allow retail to be alone in the channel for one day for new users to give time to new users to install the hub

  protected setPreviousSuccessfulConnection() {
    safeSetItem('HUB_PREVIOUS_SUCCESSFUL_CONNECTION_TO_CLIENT', moment().format());
  }
}
